import { useMemo, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { categories, hotTags } from "@/mocks/categories";
import { getNovelsByCategory, getAllNovels, type Novel } from "@/lib/novel";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";
import Breadcrumb from "@/components/base/Breadcrumb";
import NovelCard from "@/components/base/NovelCard";
import Pagination from "@/components/base/Pagination";

const PAGE_SIZE = 20;

const sortOptions = [
  { key: "updated", label: "最新更新", icon: "ri-time-line" },
  { key: "clicks", label: "阅读最多", icon: "ri-fire-line" },
  { key: "words", label: "字数最多", icon: "ri-file-text-line" },
];

export default function CategoryPage() {
  const { type = "" } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [sort, setSort] = useState("updated");
  const [tag, setTag] = useState<string | null>(null);

  const category = categories.find((item) => item.slug === type);

  useDocumentMeta(
    category
      ? `${category.name}小说 - 御书屋`
      : "分类书架 - 御书屋",
    category
      ? `${category.desc} 御书屋 ${category.name}小说专区，精选热门作品，支持在线阅读与章节浏览。`
      : "御书屋分类书架，涵盖玄幻、言情、历史等多个分类。",
  );

  const baseList: Novel[] = useMemo(() => {
    if (category) return getNovelsByCategory(type);
    return getAllNovels();
  }, [category, type]);

  const filtered = useMemo(() => {
    let list = [...baseList];
    if (tag) {
      list = list.filter((item) => item.tags.includes(tag));
    }
    if (sort === "clicks") list.sort((a, b) => b.clicks - a.clicks);
    else if (sort === "words") list.sort((a, b) => b.wordCount - a.wordCount);
    else list.sort((a, b) => (a.updatedAt < b.updatedAt ? 1 : -1));
    return list;
  }, [baseList, sort, tag]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const rawPage = Number(searchParams.get("page") || "1");
  const currentPage = Math.min(Math.max(1, Number.isNaN(rawPage) ? 1 : rawPage), totalPages);

  const pageItems = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const goPage = (page: number) => {
    const next = new URLSearchParams(searchParams);
    if (page <= 1) next.delete("page");
    else next.set("page", String(page));
    setSearchParams(next);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const updateSort = (key: string) => {
    setSort(key);
    const next = new URLSearchParams(searchParams);
    next.delete("page");
    setSearchParams(next);
  };

  const updateTag = (value: string | null) => {
    setTag((prev) => (prev === value ? null : value));
    const next = new URLSearchParams(searchParams);
    next.delete("page");
    setSearchParams(next);
  };

  const categoryName = category ? category.name : "全部";
  const title = category ? `${category.name}小说` : "全部分类";

  return (
    <div className="w-full px-4 py-8 md:px-8 md:py-10">
      <div className="mb-5">
        <Breadcrumb
          items={[
            { label: "首页", to: "/" },
            { label: "分类", to: "/category/xuanhuan" },
            { label: categoryName },
          ]}
        />
      </div>

      <header className="mb-8 rounded-lg border border-background-200 bg-background-50 p-6">
        <div className="flex flex-wrap items-center gap-3">
          <span className="flex h-12 w-12 items-center justify-center rounded-lg bg-primary-100 text-primary-600">
            <i className={`${category ? category.icon : "ri-apps-2-line"} text-2xl`}></i>
          </span>
          <div>
            <h1 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
              {title}
            </h1>
            <p className="mt-1 text-sm text-foreground-500">
              {category ? category.desc : "浏览御书屋的全部小说作品"}
            </p>
          </div>
        </div>
      </header>

      <div className="mb-6 flex flex-wrap items-center gap-2">
        <span className="mr-1 text-sm text-foreground-500">
          <i className="ri-price-tag-3-line mr-1 text-accent-500"></i>
          热门标签
        </span>
        {hotTags.map((item) => (
          <button
            key={item}
            type="button"
            onClick={() => updateTag(item)}
            className={`whitespace-nowrap rounded-full border px-3 py-1 text-xs transition-colors ${
              tag === item
                ? "border-accent-500 bg-accent-500 text-background-50"
                : "border-background-300 bg-background-50 text-foreground-600 hover:border-accent-300 hover:text-accent-600"
            }`}
          >
            {item}
          </button>
        ))}
        {tag && (
          <button
            type="button"
            onClick={() => updateTag(null)}
            className="whitespace-nowrap text-xs text-foreground-400 underline hover:text-foreground-700"
          >
            清除筛选
          </button>
        )}
      </div>

      <div className="mb-6 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-background-200 bg-background-50 px-4 py-3">
        <div className="flex flex-wrap items-center gap-2">
          <span className="mr-1 text-sm text-foreground-500">排序</span>
          {sortOptions.map((option) => (
            <button
              key={option.key}
              type="button"
              onClick={() => updateSort(option.key)}
              className={`flex items-center gap-1 whitespace-nowrap rounded-md px-3 py-1.5 text-sm transition-colors ${
                sort === option.key
                  ? "bg-primary-500 text-background-50"
                  : "bg-background-100 text-foreground-600 hover:bg-background-200"
              }`}
            >
              <i className={option.icon}></i>
              {option.label}
            </button>
          ))}
        </div>
        <span className="text-sm text-foreground-500">
          共 <strong className="text-accent-600">{filtered.length}</strong> 部作品
        </span>
      </div>

      {pageItems.length > 0 ? (
        <>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            {pageItems.map((novel) => (
              <NovelCard key={novel.id} novel={novel} />
            ))}
          </div>
          <div className="mt-8">
            <Pagination current={currentPage} total={totalPages} onChange={goPage} />
          </div>
        </>
      ) : (
        <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-50 py-20 text-center">
          <span className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-background-100 text-3xl text-foreground-300">
            <i className="ri-book-2-line"></i>
          </span>
          <h2 className="font-heading text-lg font-semibold text-foreground-800">
            暂无相关小说，换个筛选条件试试
          </h2>
          <p className="mt-2 text-sm text-foreground-500">
            当前条件下没有找到作品，你可以清除筛选或返回首页浏览热门小说。
          </p>
          <div className="mt-5 flex flex-wrap justify-center gap-3">
            <button
              type="button"
              onClick={() => {
                setTag(null);
                setSort("updated");
              }}
              className="whitespace-nowrap rounded-md border border-background-300 px-5 py-2.5 text-sm font-medium text-foreground-700 transition-colors hover:bg-background-100"
            >
              清除筛选条件
            </button>
            <Link
              to="/"
              className="whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
            >
              返回首页
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}